import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Flex, Box } from '@rebass/grid';
import { CommonCard, IconFlex } from '@amtrav/view-components';
import { IconArrowUpLong, IconLine } from '@amtrav/icons';
import { getTokenValue, THEME_TOKENS, MatchMedia } from '@amtrav/theme-system';
import { AttendeePropType } from '../meet/propTypeDefs';
import { TimeDateDisplay } from './components/TimeDateDisplay';
import { DateDisplay } from './components/DateDisplay';

const InviteDisplayCard = styled(CommonCard)`
  max-width: 860px;
  border: 1px solid ${getTokenValue(THEME_TOKENS.base.color.infoBorder)};
`;

const DateContainer = styled(Flex)`
  background-color: ${getTokenValue(THEME_TOKENS.base.color.dim1)};
  padding: 12px;
`;

const InfoCenterContainer = styled(Flex)`
  flex-direction: column;
  width: 100%;
`;

const InfoSubSection = styled(Box)`
  width: 100%;
`;

const InfoColumn = styled(Flex)`
  flex-direction: column;
  gap: 16px;
`;

// also used in Team Invites table
export const BookingLink = styled.a`
  font-size: 13px;
  font-weight: 800;
  color: ${getTokenValue(THEME_TOKENS.base.color.action)};
  text-decoration: none;
`;

const MeetingTitleContainer = styled.div`
  display: grid;
`;
const MeetingTitle = styled.div`
  font-size: 20px;
  font-weight: bold;
  line-height: 1.2;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-decoration: none;
  overflow: hidden;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;

const InfoDetail = styled(Flex)`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;

const getAdditionalGuestsText = (numberOfGuests) => {
  if (numberOfGuests === 1) {
    return `additional guest`;
  }
  return `additional guests`;
};

const InviteCard = ({ meetingInfo }) => {
  return (
    <InviteDisplayCard>
      <Flex flexDirection={['column', 'column', 'row']}>
        <MatchMedia.Medium>
          <DateContainer>
            <DateDisplay date={meetingInfo.meeting.meeting.startDate} />
          </DateContainer>
        </MatchMedia.Medium>
        <InfoCenterContainer p={['12px', '12px', '24px']}>
          <Box mb={3}>
            <Flex alignItems="flex-start" mb={1}>
              <Box>{meetingInfo.meeting.meeting.createdBy.companyName}</Box>
            </Flex>
            <MeetingTitleContainer>
              <MeetingTitle>{meetingInfo.meeting.meeting.name}</MeetingTitle>
            </MeetingTitleContainer>
          </Box>
          <InfoSubSection>
            <InfoColumn>
              <Flex alignItems="center" mb={3}>
                <Box pr={4}>
                  <TimeDateDisplay
                    time={meetingInfo.meeting.meeting.startTime}
                    date={meetingInfo.meeting.meeting.startDate.toString()}
                    label="Starts"
                  />
                </Box>
                <IconFlex defaultColor>
                  <IconArrowUpLong rotate={90} />
                </IconFlex>
                <Box pl={4}>
                  <TimeDateDisplay
                    time={meetingInfo.meeting.meeting.endTime}
                    date={meetingInfo.meeting.meeting.endDate.toString()}
                    label="Ends"
                  />
                </Box>
              </Flex>

              <Flex alignItems="center">
                <IconFlex size={18} mr="8px" defaultColor>
                  <IconLine icon="MapPin" />
                </IconFlex>
                <InfoDetail>
                  {meetingInfo.meeting.meeting.location.shortDisplay || <i>Missing Location Information</i>}
                </InfoDetail>
              </Flex>
              {meetingInfo.defaultAdditionalTravelers ? (
                <b>
                  You can bring up to&nbsp;<strong>{meetingInfo.defaultAdditionalTravelers}</strong>&nbsp;
                  {getAdditionalGuestsText(meetingInfo.defaultAdditionalTravelers)}
                </b>
              ) : null}
            </InfoColumn>
          </InfoSubSection>
        </InfoCenterContainer>
      </Flex>
    </InviteDisplayCard>
  );
};

InviteCard.propTypes = {
  bookModalHandler: PropTypes.func,
  linkToInvites: PropTypes.bool,
  inviteData: PropTypes.shape({
    attendee: AttendeePropType,
    meetingId: PropTypes.number,
    uid: PropTypes.string,
    name: PropTypes.string,
    hideBookingControls: PropTypes.bool,
    needsApproval: PropTypes.bool,
    start: PropTypes.instanceOf(Date),
    startDateFormatted: PropTypes.string,
    startDate: PropTypes.number,
    startTimeFormatted: PropTypes.string,
    endDateFormatted: PropTypes.string,
    endTimeFormatted: PropTypes.string,
    location: PropTypes.shape({
      shortDisplay: PropTypes.string,
    }),
    createdBy: PropTypes.shape({
      companyName: PropTypes.string,
    }),
  }),
};

export default InviteCard;
